import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Plus, Building2, Edit, Trash2, Users, FolderOpen, DollarSign } from 'lucide-react';
import type { AppDispatch, RootState } from '@/store';
import {
  fetchDivisions,
  createDivision,
  updateDivision,
  updateDivisionBudget,
  deleteDivision,
  clearError,
} from '@/store/slices/divisionsSlice';
import { DataTable } from '@/components/ui';
import {
  GlassCard,
  GlassButton,
  GlassBadge,
  GlassPageHeader,
  GlassModal,
  GlassConfirmModal,
  GlassLoadingSpinner,
  GlassAlert,
  SectionReveal,
  MetricCard,
  CollapsibleMetricsSection,
} from '@/design-system';
import DivisionForm from './components/DivisionForm';
import type { Division, Column } from '@/types';
import { formatCurrencyCompact, formatCurrencyFull } from '@/utils/formatters';

const DivisionsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { entities: divisions, loading, error } = useSelector((state: RootState) => state.divisions);
  const currentUser = useSelector((state: RootState) => state.auth.user);

  // Check if user can edit budget (FINANCE or ADMIN)
  const canEditBudget = useMemo(() => {
    return currentUser?.role === 'FINANCE' || currentUser?.role === 'ADMIN';
  }, [currentUser?.role]);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedDivision, setSelectedDivision] = useState<Division | null>(null);

  useEffect(() => {
    dispatch(fetchDivisions());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  const handleCreateDivision = async (data: Partial<Division>) => {
    const result = await dispatch(createDivision(data));
    if (createDivision.fulfilled.match(result)) {
      setIsCreateModalOpen(false);
    }
  };

  const handleUpdateDivision = async (data: Partial<Division>) => {
    if (!selectedDivision) return;
    const result = await dispatch(updateDivision({ id: selectedDivision.id, data }));
    if (updateDivision.fulfilled.match(result)) {
      setIsEditModalOpen(false);
      setSelectedDivision(null);
    }
  };

  const handleUpdateDivisionBudget = async (data: { annualBudget?: number; budgetFiscalYear?: number }) => {
    if (!selectedDivision) return;
    await dispatch(updateDivisionBudget({ id: selectedDivision.id, data }));
    // Note: Modal is closed by handleUpdateDivision, not here
  };

  const handleDeleteDivision = async () => {
    if (!selectedDivision) return;
    const result = await dispatch(deleteDivision(selectedDivision.id));
    if (deleteDivision.fulfilled.match(result)) {
      setIsDeleteModalOpen(false);
      setSelectedDivision(null);
    }
  };

  // Calculate metrics
  const totalPrograms = divisions.reduce((sum, d) => sum + (d._count?.programs || 0), 0);
  const divisionsWithLead = divisions.filter(d => d.leadType === 'USER' || d.leadText).length;
  const totalBudget = divisions.reduce((sum, d) => sum + (d.annualBudget || 0), 0);

  const columns: Column<Division>[] = [
    {
      key: 'name',
      header: 'Division Name',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="font-medium text-foreground text-[13px]">{value}</div>
          {row.budgetCode && (
            <div className="text-xs text-muted-foreground/60">
              Budget Code: {row.budgetCode}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'lead',
      header: 'Division Lead',
      render: (value, row) => {
        if (row.leadType === 'USER' && value) {
          return (
            <span className="text-foreground font-medium">
              {value.firstName} {value.lastName}
            </span>
          );
        } else if (row.leadType === 'TEXT' && row.leadText) {
          return <span className="text-foreground font-medium">{row.leadText}</span>;
        }
        return <span className="text-muted-foreground/60 italic">Unassigned</span>;
      },
    },
    {
      key: 'budgetCode',
      header: 'Budget Code',
      render: (value) => (
        value ? (
          <GlassBadge variant="info" size="sm">{value}</GlassBadge>
        ) : (
          <span className="text-muted-foreground/60">-</span>
        )
      ),
    },
    {
      key: 'annualBudget',
      header: 'Annual Budget',
      sortable: true,
      render: (value, row) => (
        <div className="flex flex-col">
          <span className="font-medium text-foreground">
            {formatCurrencyFull(value, { fallback: '-' })}
          </span>
          {row.budgetFiscalYear && (
            <span className="text-xs text-muted-foreground/60">
              FY{row.budgetFiscalYear}
            </span>
          )}
        </div>
      ),
    },
    {
      key: '_count',
      header: 'Programs',
      render: (value) => (
        <div className="flex items-center gap-1">
          <Users className="w-4 h-4 text-muted-foreground/60" />
          <span className="font-medium text-accent-blue">{value?.programs || 0}</span>
        </div>
      ),
    },
  ];

  const tableActions = [
    {
      label: 'Edit',
      icon: Edit,
      onClick: (division: Division) => {
        setSelectedDivision(division);
        setIsEditModalOpen(true);
      },
    },
    {
      label: 'Delete',
      icon: Trash2,
      variant: 'danger' as const,
      onClick: (division: Division) => {
        setSelectedDivision(division);
        setIsDeleteModalOpen(true);
      },
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen glass-page-bg flex items-center justify-center">
        <GlassLoadingSpinner size="lg" variant="gradient" label="Loading divisions..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen glass-page-bg p-6 lg:p-8">
      <SectionReveal>
        <GlassPageHeader
          title="Divisions"
          subtitle="Manage organizational divisions and budget allocation"
          icon={<Building2 className="w-6 h-6" />}
          iconGradient="amber"
          className="mb-6"
          actions={
            <GlassButton
              variant="primary"
              size="sm"
              onClick={() => setIsCreateModalOpen(true)}
              leftIcon={<Plus className="w-4 h-4" />}
            >
              Create Division
            </GlassButton>
          }
        />
      </SectionReveal>

      {/* Metrics Row */}
      <SectionReveal delay={0.1}>
        <CollapsibleMetricsSection storageKey="divisions">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              title="Total Divisions"
              value={divisions.length}
              icon={<Building2 className="w-5 h-5" />}
              iconColor="amber"
            />
            <MetricCard
              title="Total Programs"
              value={totalPrograms}
              icon={<FolderOpen className="w-5 h-5" />}
              iconColor="cyan"
            />
            <MetricCard
              title="With Leadership"
              value={divisionsWithLead}
              icon={<Users className="w-5 h-5" />}
              iconColor="emerald"
            />
            <MetricCard
              title="Total Budget"
              value={formatCurrencyCompact(totalBudget)}
              icon={<DollarSign className="w-5 h-5" />}
              iconColor="violet"
            />
          </div>
        </CollapsibleMetricsSection>
      </SectionReveal>

      {error && (
        <SectionReveal>
          <GlassAlert
            variant="error"
            message={error}
            onClose={() => dispatch(clearError())}
            className="mb-6"
          />
        </SectionReveal>
      )}

      <SectionReveal delay={0.2}>
        <GlassCard padding="none">
          <DataTable
            data={divisions}
            columns={columns}
            actions={tableActions}
            searchable
            sortable
            pagination
            pageSize={15}
            emptyMessage="No divisions found"
            emptyIcon={<Building2 className="w-12 h-12 text-muted-foreground/60" />}
          />
        </GlassCard>
      </SectionReveal>

      {/* Floating Create button */}
      <button
        className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-lg bg-gradient-to-r from-amber-500 to-amber-600 text-white hover:shadow-xl hover:scale-105 transition-all duration-200 flex items-center justify-center"
        onClick={() => setIsCreateModalOpen(true)}
        aria-label="Create Division"
      >
        <Plus className="w-6 h-6" />
      </button>

      {/* Create Modal */}
      <GlassModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Create Division"
        subtitle="Add a new organizational division"
        icon={<Building2 className="w-5 h-5" />}
        iconGradient="amber"
        size="lg"
      >
        <DivisionForm
          onSubmit={handleCreateDivision}
          onCancel={() => setIsCreateModalOpen(false)}
          isLoading={loading}
        />
      </GlassModal>

      {/* Edit Modal */}
      <GlassModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedDivision(null);
        }}
        title="Edit Division"
        subtitle={selectedDivision?.name}
        icon={<Edit className="w-5 h-5" />}
        iconGradient="blue-violet"
        size="lg"
      >
        <DivisionForm
          initialData={selectedDivision}
          onSubmit={handleUpdateDivision}
          onBudgetSubmit={handleUpdateDivisionBudget}
          onCancel={() => {
            setIsEditModalOpen(false);
            setSelectedDivision(null);
          }}
          isLoading={loading}
          canEditBudget={canEditBudget}
        />
      </GlassModal>

      {/* Delete Confirmation Modal */}
      <GlassConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedDivision(null);
        }}
        onConfirm={handleDeleteDivision}
        title="Delete Division"
        message={`Are you sure you want to delete "${selectedDivision?.name}"? This will affect all associated programs and cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        isLoading={loading}
      />
    </div>
  );
};

export default DivisionsPage;
